import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Sparkles, ChevronRight, Star, Clock, Users, Target } from 'lucide-react';
import { toast } from 'sonner';
import AiPlanForm from '../components/ai-plan/AiPlanForm';
import AiPlanResult from '../components/ai-plan/AiPlanResult';
import AiPlanLiabilityModal from '../components/ai-plan/AiPlanLiabilityModal';
import AiPlanQuotaBadge from '../components/ai-plan/AiPlanQuotaBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OFFLINE_CACHE_KEY = 'aiPlansCache';

function cachePlan(plan) {
  try {
    const raw = localStorage.getItem(OFFLINE_CACHE_KEY);
    const cache = raw ? JSON.parse(raw) : {};
    cache[plan.id] = { plan, cached_at: Date.now() };
    // Keep cache under control — drop entries older than 14 days
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    Object.keys(cache).forEach(id => {
      if (cache[id].cached_at < cutoff) delete cache[id];
    });
    localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(cache));
  } catch (e) { /* localStorage full or unavailable */ }
}

export default function AiTrainingPlans() {
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [latestPlan, setLatestPlan] = useState(null);
  const [recentPlans, setRecentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quotaRefreshKey, setQuotaRefreshKey] = useState(0);

  useEffect(() => {
    loadRecent();
  }, []);

  const loadRecent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/training/ai/plans`, { params: { limit: 12 } });
      setRecentPlans(res.data || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleGenerated = (planDoc) => {
    setLatestPlan(planDoc);
    setQuotaRefreshKey(k => k + 1);
    cachePlan(planDoc);
    loadRecent();
    // Smooth scroll to result
    setTimeout(() => {
      document.querySelector('[data-testid="ai-plan-result"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handlePlanUpdate = (updated) => {
    setLatestPlan(updated);
    cachePlan(updated);
    setRecentPlans(prev => prev.map(p => p.id === updated.id ? { ...p, starred: updated.starred, name: updated.name } : p));
  };

  const handlePlanDelete = (id) => {
    setLatestPlan(null);
    setRecentPlans(prev => prev.filter(p => p.id !== id));
    try {
      const raw = localStorage.getItem(OFFLINE_CACHE_KEY);
      if (raw) {
        const cache = JSON.parse(raw);
        delete cache[id];
        localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(cache));
      }
    } catch (e) { /* ignore */ }
  };

  return (
    <Layout>
      <AiPlanLiabilityModal onAcknowledged={() => setAcknowledged(true)} />

      <div className="space-y-6 page-slide-in" data-testid="ai-plans-container">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
              AI Training Plans
            </h1>
            <p className="text-gray-500 mt-1">Describe your session — get a structured plan ready for the pitch.</p>
          </div>
          <AiPlanQuotaBadge refreshKey={quotaRefreshKey} />
        </div>

        {acknowledged && (
          <AiPlanForm onGenerated={handleGenerated} />
        )}

        {latestPlan && (
          <AiPlanResult planDoc={latestPlan} onUpdate={handlePlanUpdate} onDelete={handlePlanDelete} />
        )}

        {/* Recent plans library */}
        <div data-testid="ai-plans-library">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-gray-900">Your library</h2>
            {recentPlans.length > 0 && <span className="text-sm text-gray-500">{recentPlans.length} {recentPlans.length === 1 ? 'plan' : 'plans'}</span>}
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : recentPlans.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-500">No saved plans yet — generate your first one above.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentPlans.map(p => (
                <button
                  key={p.id}
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/training/ai/plans/${p.id}`);
                      setLatestPlan(res.data);
                      cachePlan(res.data);
                      setTimeout(() => document.querySelector('[data-testid="ai-plan-result"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                    } catch (e) {
                      toast.error('Failed to load plan');
                    }
                  }}
                  className="text-left bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-emerald-300 transition-all group"
                  data-testid={`ai-plan-library-card-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="font-semibold text-gray-900 line-clamp-1">{p.name}</p>
                    {p.starred && <Star className="w-4 h-4 fill-amber-400 text-amber-500 flex-shrink-0" />}
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">{p.summary || 'No summary'}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.total_duration_min} min</span>
                    {p.focus_area && <span className="inline-flex items-center gap-1"><Target className="w-3 h-3" />{p.focus_area}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 group-hover:text-emerald-600 transition-all" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
